import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { CanvasElement } from './templateSerializer';
import './templateBuilder.css';

interface ResizableCanvasItemProps {
  item: CanvasElement;
  itemIndex: number;
  secCols: number;
  sectionId: string;
  onUpdateSpan: (secId: string, itemId: string, newSpan: number) => void;
  onUpdateContent?: (secId: string, itemId: string, content: string) => void;
  onEditElement?: (item: CanvasElement, secId: string, maxCols: number) => void;
  onToggleIf?: (secId: string, itemId: string) => void;
  onDuplicateItem: (secId: string, item: CanvasElement) => void;
  onRemoveItem: (secId: string, itemId: string) => void;
  onReorderItems: (
    sourceSecId: string,
    targetSecId: string,
    sourceIndex: number,
    targetIndex: number,
    isBeside?: boolean
  ) => void;
  onInsertItemAtIndex: (targetSecId: string, targetIndex: number, data: any, isBeside?: boolean) => void;
}

const heightSteps = ['h-32', 'h-48', 'h-64', 'h-80', 'h-96'];

const ResizableCanvasItem: React.FC<ResizableCanvasItemProps> = ({
  item,
  itemIndex,
  secCols = 6,
  sectionId,
  onUpdateSpan,
  onUpdateContent,
  onToggleIf,
  onDuplicateItem,
  onRemoveItem,
  onReorderItems,
  onInsertItemAtIndex,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isResizingX, setIsResizingX] = useState(false);
  const [dropSide, setDropSide] = useState<'left' | 'right' | 'bottom' | null>(null);
  const [liveSpan, setLiveSpan] = useState<number | null>(null);

  const colSpanVal = liveSpan !== null ? liveSpan : Math.min(item.colSpan || 1, secCols);

  // DRAG REORDER HANDLERS
  const handleItemDragStart = (e: React.DragEvent) => {
    e.stopPropagation();
    const payload = {
      type: 'canvasItem',
      sourceSecId: sectionId,
      sourceIndex: itemIndex,
      item,
    };
    (window as any).__tb_dragged_item = payload;
    if (e.dataTransfer) {
      try {
        const jsonStr = JSON.stringify(payload);
        e.dataTransfer.setData('text/plain', jsonStr);
        e.dataTransfer.setData('application/json', jsonStr);
        e.dataTransfer.effectAllowed = 'copyMove';
      } catch (err) { }
    }
  };

  const handleItemDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }

    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      const mouseX = e.clientX;
      const mouseY = e.clientY;

      const relY = (mouseY - rect.top) / rect.height;

      if (relY > 0.65) {
        setDropSide('bottom');
      } else {
        const isRight = mouseX > rect.left + rect.width / 2;
        setDropSide(isRight ? 'right' : 'left');
      }
    }
  };

  const handleItemDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDropSide(null);
    }
  };

  const handleItemDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropSide(null);

    // EMPIRICALLY DETERMINE DROP POSITION FROM MOUSE COORDINATES AT DROP INSTANT
    let calculatedSide: 'left' | 'right' | 'bottom' = 'bottom';
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      const mouseX = e.clientX;
      const mouseY = e.clientY;
      const relY = (mouseY - rect.top) / rect.height;

      if (relY > 0.65) {
        calculatedSide = 'bottom';
      } else {
        const isRight = mouseX > rect.left + rect.width / 2;
        calculatedSide = isRight ? 'right' : 'left';
      }
    }

    let data = (window as any).__tb_dragged_item;
    if (!data && e.dataTransfer) {
      const dataStr =
        e.dataTransfer.getData('application/json') || e.dataTransfer.getData('text/plain');
      if (dataStr) {
        try {
          data = JSON.parse(dataStr);
        } catch (err) { }
      }
    }

    if (!data) return;

    try {
      const targetIdx = calculatedSide === 'right' || calculatedSide === 'bottom' ? itemIndex + 1 : itemIndex;
      const isBeside = calculatedSide === 'left' || calculatedSide === 'right';

      if (data.type === 'canvasItem') {
        onReorderItems(data.sourceSecId, sectionId, data.sourceIndex, targetIdx, isBeside);
      } else if (data.type === 'key' || data.type === 'custom') {
        onInsertItemAtIndex(sectionId, targetIdx, data, isBeside);
      }
    } catch (err) {
      console.error('Failed to handle item drop:', err);
    } finally {
      (window as any).__tb_dragged_item = null;
    }
  };

  // HORIZONTAL (RIGHT BORDER) DRAG RESIZE VIA MOUSE DOWN
  const handleRightHandleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!cardRef.current) return;
    const parentContainer = cardRef.current.closest('.row');
    if (!parentContainer) return;

    const containerWidth = parentContainer.getBoundingClientRect().width;
    const singleColWidth = containerWidth / secCols;
    const startX = e.clientX;
    const initialSpan = item.colSpan || 1;

    setIsResizingX(true);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      const deltaX = moveEvent.clientX - startX;
      const deltaCols = Math.round(deltaX / singleColWidth);
      const calculatedSpan = Math.max(1, Math.min(secCols, initialSpan + deltaCols));
      setLiveSpan(calculatedSpan);
    };

    const handleMouseUp = (upEvent: MouseEvent) => {
      upEvent.preventDefault();
      setIsResizingX(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      const deltaX = upEvent.clientX - startX;
      const deltaCols = Math.round(deltaX / singleColWidth);
      const finalSpan = Math.max(1, Math.min(secCols, initialSpan + deltaCols));
      setLiveSpan(null);
      onUpdateSpan(sectionId, item.id, finalSpan);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const isPhoto = (item.keyType || '').toLowerCase() === 'photo';
  const isMultiple = (item.keyType || '').toLowerCase() === 'multiple';
  const percentWidth = Math.round((colSpanVal / 6) * 100);

  // Bootstrap 12-col mapping from 6-col grid span
  const bsColClass =
    colSpanVal >= 6 ? 'col-12' : `col-12 col-md-${Math.min(12, colSpanVal * 2)}`;

  return (
    <div
      className={`${bsColClass} position-relative`}
      onDragOver={handleItemDragOver}
      onDragLeave={handleItemDragLeave}
      onDrop={handleItemDrop}
    >
      {/* ANIMATED INSERTION INDICATOR LINE (LEFT BESIDE, RIGHT BESIDE, OR BOTTOM NEW ROW) */}
      {dropSide === 'left' && (
        <motion.div
          initial={{ opacity: 0, scaleY: 0.5 }}
          animate={{ opacity: 1, scaleY: 1 }}
          className="tb-drop-indicator-left"
        />
      )}
      {dropSide === 'right' && (
        <motion.div
          initial={{ opacity: 0, scaleY: 0.5 }}
          animate={{ opacity: 1, scaleY: 1 }}
          className="tb-drop-indicator-right"
        />
      )}
      {dropSide === 'bottom' && (
        <motion.div
          initial={{ opacity: 0, scaleX: 0.5 }}
          animate={{ opacity: 1, scaleX: 1 }}
          className="tb-drop-indicator-bottom"
        />
      )}

      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ type: 'spring', damping: 25, stiffness: 350 }}
        ref={cardRef}
        className={`p-3 tb-resizable-card ${isResizingX ? 'is-resizing' : ''}`}
        style={{
          borderLeft:
            item.type === 'key'
              ? isPhoto
                ? '4px solid #10b981'
                : isMultiple
                  ? '4px solid #8b5cf6'
                  : '4px solid #3b82f6'
              : '4px solid #64748b',
        }}
      >
        {/* RESIZE TOOLTIP BADGE */}
        {isResizingX && <div className="tb-resize-tooltip">{`Width: ${percentWidth}%`}</div>}

        {/* MINIMALIST ITEM TOOLBAR WITH DEDICATED DRAG GRIP */}
        <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
          {/* DRAG HANDLE CONTAINER - DRAGGABLE FOR ELEMENT REORDERING */}
          <div
            className="d-flex align-items-center gap-2 overflow-hidden me-2 cursor-grab select-none"
            draggable={true}
            onDragStart={handleItemDragStart}
            title={item.keyName || item.content || item.keyCode || 'Click and drag to reorder'}
            style={{ userSelect: 'none' }}
          >
            <i className="fas fa-grip-vertical text-secondary ms-1 me-1"></i>
            {item.type !== 'key' && (
              <strong
                className="small text-truncate text-dark"
                style={{ fontSize: '12px', maxWidth: '200px' }}
                title={item.content || item.type}
              >
                {item.content || item.type}
              </strong>
            )}
          </div>

          {/* SLEEK MINIMALIST ACTION BUTTONS */}
          <div className="d-flex align-items-center gap-1 flex-shrink-0">
            {/* TOGGLE IF */}
            {item.type === 'key' && (
              <div
                className="form-check form-switch m-0 d-flex align-items-center me-1"
                title={item.enableIf ? 'Anotasi {{#if}} aktif' : 'Anotasi {{#if}} nonaktif'}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  className="form-check-input m-0 cursor-pointer"
                  type="checkbox"
                  id={`toggle-if-${item.id}`}
                  checked={Boolean(item.enableIf)}
                  onChange={() => onToggleIf?.(sectionId, item.id)}
                  style={{ width: '26px', height: '14px', cursor: 'pointer' }}
                />
                <label
                  htmlFor={`toggle-if-${item.id}`}
                  className="ms-1 mb-0 font-monospace cursor-pointer user-select-none"
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: item.enableIf ? '#2563eb' : '#94a3b8',
                  }}
                >
                  if
                </label>
              </div>
            )}
            <button
              type="button"
              className="tb-btn tb-btn-icon"
              title="Duplicate"
              onClick={(e) => {
                e.stopPropagation();
                onDuplicateItem(sectionId, item);
              }}
            >
              <i className="fas fa-copy text-secondary" style={{ fontSize: '11px' }}></i>
            </button>
            <button
              type="button"
              className="tb-btn tb-btn-icon tb-btn-danger"
              title="Delete"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveItem(sectionId, item.id);
              }}
            >
              <i className="fas fa-trash-alt" style={{ fontSize: '11px' }}></i>
            </button>
          </div>
        </div>

        {/* PREVIEW DISPLAY & INLINE EDITING */}
        <div className="py-1 position-relative">
          {item.type === 'key' && (
            <>
              {item.showLabel !== false && !isMultiple && (
                <p
                  className="text-uppercase text-secondary fw-semibold mb-1"
                  style={{ fontSize: '10px', letterSpacing: '0.5px' }}
                >
                  {item.customLabel || item.keyName || item.keyCode}
                </p>
              )}

              {isPhoto ? (
                <div
                  className="rounded-3 bg-light border d-flex flex-column align-items-center justify-content-center text-muted py-3 transition h-48"
                  style={{ minHeight: '120px' }}
                >
                  <i className="fas fa-image fa-2xl mb-2 text-success opacity-75"></i>
                  <span className="small fw-semibold text-secondary">
                    Sample Photo Image
                  </span>
                </div>
              ) : isMultiple ? (
                <div className="d-flex flex-wrap gap-2 pt-1">
                  <span
                    className="badge px-3 py-2 fw-semibold text-white shadow-xs"
                    style={{ backgroundColor: '#1d72f3', borderRadius: '8px', fontSize: '12px' }}
                  >
                    Sample value 1
                  </span>
                  <span
                    className="badge px-3 py-2 fw-semibold text-white shadow-xs"
                    style={{ backgroundColor: '#1d72f3', borderRadius: '8px', fontSize: '12px' }}
                  >
                    Sample value 2
                  </span>
                  <span
                    className="badge px-3 py-2 fw-semibold text-white shadow-xs"
                    style={{ backgroundColor: '#1d72f3', borderRadius: '8px', fontSize: '12px' }}
                  >
                    Sample value 3
                  </span>
                </div>
              ) : (
                <p className="fw-medium text-dark mb-0 text-truncate" style={{ fontSize: '13px' }}>
                  Sample Text Value
                </p>
              )}
            </>
          )}

          {/* INLINE EDITABLE HEADER TITLE */}
          {item.type === 'heading' && (
            <div className="py-1">
              <label className="text-uppercase text-secondary fw-semibold mb-1 d-block" style={{ fontSize: '9px' }}>
                Header Title (Type below to edit):
              </label>
              <input
                type="text"
                className="form-control form-control-sm border-secondary-subtle bg-light fw-bold text-dark text-uppercase tracking-wider px-2 py-1"
                style={{ fontSize: '13px' }}
                value={item.content || ''}
                placeholder="Type header title..."
                onChange={(e) => {
                  if (onUpdateContent) {
                    onUpdateContent(sectionId, item.id, e.target.value);
                  }
                }}
              />
            </div>
          )}

          {/* INLINE EDITABLE CUSTOM TEXT AREA */}
          {item.type === 'text' && (
            <div className="py-1">
              <label className="text-uppercase text-secondary fw-semibold mb-1 d-block" style={{ fontSize: '9px' }}>
                Custom Text (Type below to edit):
              </label>
              <textarea
                className="form-control form-control-sm border-secondary-subtle bg-light text-dark small px-2 py-1"
                style={{ fontSize: '12px', resize: 'vertical', minHeight: '44px' }}
                rows={2}
                value={item.content || ''}
                placeholder="Type custom text block..."
                onChange={(e) => {
                  if (onUpdateContent) {
                    onUpdateContent(sectionId, item.id, e.target.value);
                  }
                }}
              />
            </div>
          )}

          {item.type === 'divider' && (
            <div className="py-1">
              <hr className="my-1 border-gray-300" />
            </div>
          )}
        </div>

        {/* RIGHT BORDER RESIZE HANDLE */}
        <div
          className={`tb-resize-handle-right ${isResizingX ? 'is-active' : ''}`}
          onMouseDown={handleRightHandleMouseDown}
          title="Drag border left/right to resize column width"
        />
      </motion.div>
    </div>
  );
};

export default ResizableCanvasItem;
